import type { BoardItem, Element, Profile, User } from "@fusioncraft/shared";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { api } from "../api";
import { AccountDialog } from "./AccountDialog";
import { tintStyle } from "../emojiTint";
import { Dialog } from "./Dialog";
import { Ledger } from "./Ledger";
import { ProfileSwitcher } from "./ProfileSwitcher";
import { ProfilesDialog } from "./ProfilesDialog";

interface Item extends BoardItem {
  /** Set while the two source items are being fused; kept so they can be restored on failure. */
  pending?: boolean;
  /** Freshly made results are centred on this point once their size is known. */
  anchor?: { cx: number; cy: number };
}

type Flash = "new" | "first" | "known";

interface Drag {
  uid: string;
  elementId: number;
  offsetX: number;
  offsetY: number;
  startX: number;
  startY: number;
  /** Ledger drags only become board items once the pointer has moved a little. */
  fromLedger: boolean;
  materialized: boolean;
  /** Past the drag threshold; until then a press on a board item is a tap (see DOUBLE_TAP_MS). */
  moved: boolean;
  target: string | null;
}

const DRAG_THRESHOLD = 4;
const DOUBLE_TAP_MS = 350;
const SAVE_DELAY = 800;

const newUid = () => crypto.randomUUID().slice(0, 12);

function serialize(items: Item[]): BoardItem[] {
  return items.map((i) => ({ uid: i.uid, elementId: i.elementId, x: i.x, y: i.y }));
}

export function Game({
  user,
  profiles,
  activeId,
  onSwitch,
  onProfilesChanged,
  onUserChanged,
  onSignOut,
}: {
  user: User;
  profiles: Profile[];
  activeId: string;
  onSwitch: (id: string) => void;
  onProfilesChanged: () => Promise<Profile[]>;
  onUserChanged: (u: User) => void;
  onSignOut: () => void;
}) {
  const profile = profiles.find((p) => p.id === activeId)!;
  const [elements, setElements] = useState<Element[] | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [firsts, setFirsts] = useState<Set<string>>(() => new Set());
  const [flashes, setFlashes] = useState<Record<string, Flash>>({});
  const [draggingUid, setDraggingUid] = useState<string | null>(null);
  const [dialog, setDialog] = useState<null | "account" | "profiles" | "new-profile" | "reset">(null);
  /** Bumped on reset so fusions still in flight can't put their results back afterwards. */
  const generation = useRef(0);

  const benchRef = useRef<HTMLDivElement>(null);
  const ledgerRef = useRef<HTMLElement>(null);
  const nodes = useRef(new Map<string, HTMLDivElement>());
  const drag = useRef<Drag | null>(null);
  const suppressClick = useRef(false);
  const lastTap = useRef<{ uid: string; at: number } | null>(null);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const loaded = useRef(false);

  // Boards sync across devices, so pieces placed on a wide screen are pulled into view on a narrow one.
  const [benchSize, setBenchSize] = useState({ w: Infinity, h: Infinity });
  useEffect(() => {
    const el = benchRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setBenchSize({ w: entry!.contentRect.width, h: entry!.contentRect.height }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const byId = useMemo(() => new Map((elements ?? []).map((e) => [e.id, e])), [elements]);

  // ---- loading & cloud sync -------------------------------------------------

  useEffect(() => {
    api
      .state(activeId)
      .then((s) => {
        setElements(s.elements);
        setItems(s.board);
        loaded.current = true;
      })
      .catch((e) => setLoadError(e.message));
  }, [activeId]);

  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flush = useCallback(
    (keepalive = false) => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = null;
      api.saveBoard(activeId, serialize(itemsRef.current), keepalive).catch((e) => setError(`Board not saved: ${e.message}`));
    },
    [activeId],
  );

  useEffect(() => {
    if (!loaded.current) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(flush, SAVE_DELAY);
  }, [items, flush]);

  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === "hidden" && saveTimer.current) flush(true);
    };
    document.addEventListener("visibilitychange", onHide);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      if (saveTimer.current) flush(true);
    };
  }, [flush]);

  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(null), 6000);
    return () => clearTimeout(t);
  }, [error]);

  // ---- fusing ----------------------------------------------------------------

  const flash = useCallback((uid: string, kind: Flash) => {
    setFlashes((f) => ({ ...f, [uid]: kind }));
    setTimeout(() => {
      setFlashes((f) => {
        const { [uid]: _, ...rest } = f;
        return rest;
      });
    }, 2400);
  }, []);

  const fuse = useCallback(
    async (dragged: BoardItem, target: BoardItem) => {
      const a = byId.get(dragged.elementId);
      const b = byId.get(target.elementId);
      if (!a || !b) return;
      const pair = new Set([dragged.uid, target.uid]);

      // Both pieces stay where they are and wait together; the result lands centred on the target.
      const node = nodes.current.get(target.uid);
      const cx = (node?.offsetLeft ?? target.x) + (node?.offsetWidth ?? 0) / 2;
      const cy = (node?.offsetTop ?? target.y) + (node?.offsetHeight ?? 0) / 2;
      setItems((list) => [
        ...list.filter((i) => i.uid !== dragged.uid).map((i) => (i.uid === target.uid ? { ...i, pending: true } : i)),
        { ...dragged, pending: true },
      ]);

      const gen = generation.current;
      try {
        const res = await api.combine(activeId, a.id, b.id);
        if (gen !== generation.current) return;
        if (res.isNew) setElements((els) => (els && !els.some((e) => e.id === res.element.id) ? [...els, res.element] : els));
        const uid = newUid();
        setItems((list) => [
          ...list.filter((i) => !pair.has(i.uid)),
          { uid, elementId: res.element.id, x: cx, y: cy, anchor: { cx, cy } },
        ]);
        flash(uid, res.firstDiscovery ? "first" : res.isNew ? "new" : "known");
        if (res.firstDiscovery) setFirsts((set) => new Set(set).add(uid));
      } catch (err) {
        if (gen !== generation.current) return;
        setItems((list) => list.map((i) => (pair.has(i.uid) ? { uid: i.uid, elementId: i.elementId, x: i.x, y: i.y } : i)));
        setError((err as Error).message);
      }
    },
    [activeId, byId, flash],
  );

  useLayoutEffect(() => {
    if (!items.some((i) => i.anchor)) return;
    setItems((list) =>
      list.map((i) => {
        if (!i.anchor) return i;
        const node = nodes.current.get(i.uid);
        const w = node?.offsetWidth ?? 0;
        const h = node?.offsetHeight ?? 0;
        return { uid: i.uid, elementId: i.elementId, x: Math.max(0, i.anchor.cx - w / 2), y: Math.max(0, i.anchor.cy - h / 2) };
      }),
    );
  }, [items]);

  // ---- dragging --------------------------------------------------------------

  const benchPoint = (clientX: number, clientY: number) => {
    const r = benchRef.current!.getBoundingClientRect();
    return { x: clientX - r.left, y: clientY - r.top, rect: r };
  };

  const hitTest = (clientX: number, clientY: number, exclude: string): string | null => {
    for (const el of document.elementsFromPoint(clientX, clientY)) {
      const uid = (el as HTMLElement).dataset?.uid;
      if (uid && uid !== exclude) {
        const item = itemsRef.current.find((i) => i.uid === uid);
        return item && !item.pending ? uid : null;
      }
    }
    return null;
  };

  const overLedger = (clientX: number, clientY: number) => {
    const r = ledgerRef.current?.getBoundingClientRect();
    return !!r && clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom;
  };

  const setTarget = (uid: string | null) => {
    const d = drag.current!;
    if (d.target === uid) return;
    if (d.target) nodes.current.get(d.target)?.classList.remove("is-target");
    if (uid) nodes.current.get(uid)?.classList.add("is-target");
    d.target = uid;
  };

  const onMove = useCallback((e: PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    if (!d.moved) {
      if (Math.hypot(e.clientX - d.startX, e.clientY - d.startY) < DRAG_THRESHOLD) return;
      d.moved = true;
      if (!d.materialized) {
        d.materialized = true;
        suppressClick.current = true;
        const p = benchPoint(e.clientX, e.clientY);
        setItems((list) => [...list, { uid: d.uid, elementId: d.elementId, x: p.x - d.offsetX, y: p.y - d.offsetY }]);
      }
      setDraggingUid(d.uid);
      document.body.classList.add("is-dragging-global");
    }
    const node = nodes.current.get(d.uid);
    if (node) {
      const p = benchPoint(e.clientX, e.clientY);
      node.style.left = `${p.x - d.offsetX}px`;
      node.style.top = `${p.y - d.offsetY}px`;
    }
    setTarget(hitTest(e.clientX, e.clientY, d.uid));
    ledgerRef.current?.classList.toggle("is-drop-discard", overLedger(e.clientX, e.clientY));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onUp = useCallback(
    (e: PointerEvent) => {
      const d = drag.current;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      document.body.classList.remove("is-dragging-global");
      ledgerRef.current?.classList.remove("is-drop-discard");
      if (!d) return;
      setTarget(null);
      drag.current = null;
      setDraggingUid(null);
      if (!d.materialized) return;
      setTimeout(() => (suppressClick.current = false), 0);

      // A press that never moved is a tap; two quick taps on the same piece duplicate it.
      if (!d.moved) {
        const now = performance.now();
        const last = lastTap.current;
        if (last && last.uid === d.uid && now - last.at < DOUBLE_TAP_MS) {
          lastTap.current = null;
          const item = itemsRef.current.find((i) => i.uid === d.uid);
          if (item && !item.pending) duplicate(item);
        } else {
          lastTap.current = { uid: d.uid, at: now };
        }
        return;
      }

      if (overLedger(e.clientX, e.clientY) || e.type === "pointercancel") {
        setItems((list) => list.filter((i) => i.uid !== d.uid));
        return;
      }

      const { x, y, rect } = benchPoint(e.clientX, e.clientY);
      const node = nodes.current.get(d.uid);
      const w = node?.offsetWidth ?? 100;
      const h = node?.offsetHeight ?? 40;
      const dropped: BoardItem = {
        uid: d.uid,
        elementId: d.elementId,
        x: Math.max(4, Math.min(rect.width - w - 4, x - d.offsetX)),
        y: Math.max(4, Math.min(rect.height - h - 4, y - d.offsetY)),
      };

      const targetUid = hitTest(e.clientX, e.clientY, d.uid);
      const target = targetUid ? itemsRef.current.find((i) => i.uid === targetUid) : undefined;
      if (target) {
        fuse(dropped, target);
      } else {
        // Re-append so the dropped item sits on top.
        setItems((list) => [...list.filter((i) => i.uid !== d.uid), dropped]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fuse, onMove],
  );

  const beginDrag = (e: ReactPointerEvent, d: Omit<Drag, "startX" | "startY" | "moved" | "target">) => {
    if (e.button !== 0) return;
    drag.current = { ...d, startX: e.clientX, startY: e.clientY, moved: false, target: null };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  };

  const onItemPointerDown = (e: ReactPointerEvent<HTMLDivElement>, item: Item) => {
    if (item.pending) return;
    e.preventDefault();
    const r = e.currentTarget.getBoundingClientRect();
    beginDrag(e, {
      uid: item.uid,
      elementId: item.elementId,
      offsetX: e.clientX - r.left,
      offsetY: e.clientY - r.top,
      fromLedger: false,
      materialized: true,
    });
  };

  const onLedgerPointerDown = (e: ReactPointerEvent, el: Element) => {
    // On touch screens the ledger scrolls; tapping a row drops the element in instead.
    if (e.pointerType === "touch") return;
    beginDrag(e, { uid: newUid(), elementId: el.id, offsetX: 20, offsetY: 22, fromLedger: true, materialized: false });
  };

  const placeNear = (el: Element) => {
    if (suppressClick.current) return;
    const r = benchRef.current!.getBoundingClientRect();
    const jitter = () => (Math.random() - 0.5) * 160;
    setItems((list) => [
      ...list,
      { uid: newUid(), elementId: el.id, x: Math.max(8, r.width / 2 - 60 + jitter()), y: Math.max(8, r.height / 2 - 20 + jitter()) },
    ]);
  };

  const duplicate = (item: Item) =>
    setItems((list) => [...list, { uid: newUid(), elementId: item.elementId, x: item.x + 18, y: item.y + 18 }]);

  const remove = (uid: string) => setItems((list) => list.filter((i) => i.uid !== uid));

  const clear = () => setItems((list) => list.filter((i) => i.pending));

  const reset = async () => {
    const state = await api.resetProfile(activeId);
    generation.current++;
    setElements(state.elements);
    setItems(state.board);
    setFirsts(new Set());
    setDialog(null);
    onProfilesChanged().catch(() => {});
  };

  // ---- render ----------------------------------------------------------------

  if (loadError) {
    return (
      <div className="splash">
        <p>{loadError}</p>
        <button className="btn" onClick={() => location.reload()}>Retry</button>
      </div>
    );
  }

  return (
    <div className="game">
      <section className="bench" ref={benchRef} aria-label="Crafting area">

        {elements === null && <div className="bench-loading">Loading…</div>}

        {items.map((item) => {
          const el = byId.get(item.elementId);
          if (!el) return null;
          const f = flashes[item.uid];
          const tint = tintStyle(el.emoji);
          const cls = [
            "instance",
            tint && "tinted",
            item.pending && "is-pending",
            draggingUid === item.uid && "is-dragging",
            f && "is-fresh",
            firsts.has(item.uid) && "is-discovery",
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <div
              key={item.uid}
              data-uid={item.uid}
              ref={(n) => {
                if (n) nodes.current.set(item.uid, n);
                else nodes.current.delete(item.uid);
              }}
              className={cls}
              style={{
                ...tint,
                left: Math.max(0, Math.min(item.x, benchSize.w - 160)),
                top: Math.max(0, Math.min(item.y, benchSize.h - 50)),
              }}
              onPointerDown={(e) => onItemPointerDown(e, item)}
              onContextMenu={(e) => {
                e.preventDefault();
                if (!item.pending) remove(item.uid);
              }}
            >
              <span className="instance-emoji">{el.emoji}</span>
              {el.name}
            </div>
          );
        })}

        <div className="corner-controls">
          <ProfileSwitcher
            profiles={profiles}
            active={profile}
            onSwitch={onSwitch}
            onManage={() => setDialog("profiles")}
            onNew={() => setDialog("new-profile")}
          />
          <button className="square-btn" onClick={() => setDialog("account")} aria-label="Account" title={user.email}>
            <svg viewBox="0 0 20 20" aria-hidden="true">
              <circle cx="10" cy="7" r="3.5" />
              <path d="M3.5 17c.8-3.3 3.4-5 6.5-5s5.7 1.7 6.5 5" />
            </svg>
            {!user.totpEnabled && <span className="dot-hint" />}
          </button>
        </div>

        <div className="side-controls">
          <button className="tool" data-tooltip="Clear" onClick={clear} aria-label="Clear the crafting area">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M19.5 3.5 12 11M8.5 9.5l6 6M8.5 9.5c-2 .5-4.5 2-5.5 4l3 3 1-1-.5 2 2 2 1.5-1.5v2l1 1c2-1 3.5-3.5 4-5.5" />
            </svg>
          </button>
          <button className="tool" data-tooltip="Reset progress" onClick={() => setDialog("reset")} aria-label="Reset progress">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M4 12a8 8 0 1 0 2.4-5.7L4 8.5M4 4v4.5h4.5" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="toast" role="alert" onClick={() => setError(null)}>
            {error}
          </div>
        )}
      </section>

      <Ledger ref={ledgerRef} elements={elements ?? []} onPick={placeNear} onDragStart={onLedgerPointerDown} />

      {dialog === "reset" && (
        <ResetDialog profile={profile} onConfirm={reset} onClose={() => setDialog(null)} />
      )}
      {dialog === "account" && (
        <AccountDialog user={user} onClose={() => setDialog(null)} onUserChanged={onUserChanged} onSignOut={onSignOut} />
      )}
      {(dialog === "profiles" || dialog === "new-profile") && (
        <ProfilesDialog
          profiles={profiles}
          activeId={activeId}
          startWithForm={dialog === "new-profile"}
          onSwitch={onSwitch}
          onChanged={onProfilesChanged}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
}

function ResetDialog({ profile, onConfirm, onClose }: { profile: Profile; onConfirm: () => Promise<void>; onClose: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <Dialog title="Reset progress?" onClose={onClose}>
      <div className="stack">
        <p>
          <strong>{profile.name}</strong> goes back to Water, Fire, Wind and Earth, and the crafting area is cleared.
        </p>
        <p className="muted">
          Every combination this profile has made stays cached, so crafting them again is instant and costs nothing.
        </p>
        {error && <p className="form-error">{error}</p>}
        <div className="form-actions">
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-danger"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await onConfirm();
              } catch (err) {
                setError((err as Error).message);
                setBusy(false);
              }
            }}
          >
            {busy ? "Resetting…" : "Reset"}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
